import { motion } from "framer-motion";
import { ShieldCheck, Sun, FileBadge, Zap, Award } from "lucide-react";

/**
 * Credentials — compact trust strip that sits in the home flow (after Marquee).
 * Displays the company's active EPRA + NCA registrations.
 * Shows: authority badge, description, registration number(s), Active status.
 * Class and validity dates are intentionally hidden — copies are issued on request.
 */
const CREDS = [
  {
    authority: "EPRA",
    icon: Sun,
    title: "Solar PV Equipment — Manufacturer & Importer",
    numbers: ["EPRA/SPVM/000852"],
    accent: "var(--accent-blue)",
  },
  {
    authority: "EPRA",
    icon: FileBadge,
    title: "Solar PV Systems — Contractor & Vendor",
    numbers: ["EPRA/SPVC/02059", "EPRA/SPVC/02060"],
    accent: "var(--accent-blue)",
  },
  {
    authority: "EPRA",
    icon: Zap,
    title: "Electrical Contractor — Class A",
    numbers: ["EPRA/EC/05135"],
    accent: "var(--accent-crimson-hover)",
  },
  {
    authority: "NCA",
    icon: Award,
    title: "Electrical Engineering · Solar Power & PV Installations",
    numbers: ["94894/E/0825"],
    accent: "var(--accent-crimson-hover)",
  },
];

const Licenses = () => {
  return (
    <section
      id="licenses"
      aria-label="Active EPRA and NCA registrations"
      data-testid="credentials-strip"
      className="relative overflow-hidden"
      style={{
        background: "var(--bg)",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div className="absolute inset-0 energy-grid-bg opacity-[0.12] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-10">
        <div className="flex items-center gap-3 mb-5">
          <ShieldCheck
            className="w-3.5 h-3.5"
            strokeWidth={1.8}
            style={{ color: "var(--accent-blue)" }}
          />
          <span className="eyebrow">Licensed · EPRA & NCA</span>
          <span
            aria-hidden
            className="flex-1 h-px"
            style={{
              background:
                "linear-gradient(90deg, var(--accent-blue), transparent)",
              opacity: 0.4,
            }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px"
             style={{ background: "var(--border)" }}>
          {CREDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.article
                key={`${c.authority}-${c.numbers[0]}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`credential-${i}`}
                className="group relative p-5 lg:p-6 transition-colors duration-300"
                style={{ background: "var(--bg)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--surface)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "var(--bg)")
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <span
                    className="w-9 h-9 flex items-center justify-center btn-sharp shrink-0"
                    style={{
                      border: `1px solid ${c.accent}`,
                      color: c.accent,
                      background: "var(--bg-soft)",
                    }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.6} />
                  </span>
                  <span
                    className="font-mono-spec text-[10px] tracking-[0.22em] uppercase px-2 py-1 btn-sharp"
                    style={{
                      border: `1px solid ${c.accent}`,
                      color: c.accent,
                      background: "var(--surface-glass)",
                    }}
                    data-testid={`credential-authority-${i}`}
                  >
                    {c.authority}
                  </span>
                </div>

                <h3
                  className="mt-4 font-display text-[14px] font-medium leading-snug"
                  style={{ color: "var(--text)" }}
                >
                  {c.title}
                </h3>

                <ul className="mt-3 space-y-1">
                  {c.numbers.map((n) => (
                    <li
                      key={n}
                      className="font-mono-spec text-[11px] tracking-[0.18em] uppercase break-all"
                      style={{ color: "var(--text-muted)" }}
                      data-testid={`credential-number-${i}`}
                    >
                      {n}
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{
                      background: "#22c55e",
                      boxShadow: "0 0 8px rgba(34,197,94,0.7)",
                    }}
                  />
                  <span
                    className="font-mono-spec text-[10px] tracking-[0.22em] uppercase"
                    style={{ color: "#22c55e" }}
                  >
                    Active
                  </span>
                </div>

                <span
                  aria-hidden
                  className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-700"
                  style={{ background: c.accent }}
                />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Licenses;
